/**
 * Pools listing page — uses PageHeader + Card + Badge + EmptyState + Skeleton.
 */

import { Link } from 'react-router-dom';
import { Users, Plus, Trophy, Lock, Globe } from 'lucide-react';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/card';
import Badge from '../components/ui/badge';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import { usePools } from '../hooks/usePools';
import { cn } from '../lib/utils';
import type { Pool } from '../../shared/types';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

export default function PoolsPage() {
  useSEO(SEO_CONFIGS.pools);
  const { data: pools, isLoading } = usePools();

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        title="Bolões"
        description="Acompanhe seus bolões, convide amigos e dispute prêmios na Copa do Mundo 2026."
        actions={
          <Link to="/boloes/criar">
            <Button icon={<Plus />}>Criar bolão</Button>
          </Link>
        }
      />

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : !pools || pools.length === 0 ? (
        <EmptyState
          icon={<Users className="w-6 h-6" aria-hidden="true" />}
          title="Nenhum bolão disponível"
          description="Crie o primeiro bolão e convide seus amigos para começar a apostar."
          action={
            <Link to="/boloes/criar">
              <Button icon={<Plus />}>Criar bolão</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pools.map((pool: Pool, index: number) => (
            <PoolCard key={pool.id} pool={pool} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

function PoolCard({ pool, index }: { pool: Pool; index: number }) {
  return (
    <Link
      to={`/boloes/${pool.slug}`}
      className={cn(
        'group block rounded-2xl animate-fade-in',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      )}
      style={{ animationDelay: `${Math.min(index * 50, 250)}ms` }}
    >
      <Card className="p-5 h-full transition-all duration-200 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-lg dark:hover:shadow-dark-lg group-hover:-translate-y-0.5">
        <div className="flex items-start justify-between mb-4">
          <div
            className={cn(
              'flex h-11 w-11 items-center justify-center rounded-xl',
              pool.isDemo
                ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
                : 'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300'
            )}
          >
            <Trophy className="h-5 w-5" aria-hidden="true" />
          </div>
          {pool.isDemo ? (
            <Badge variant="warning">Demo</Badge>
          ) : pool.isPublic ? (
            <Badge variant="success">
              <Globe className="w-3 h-3" aria-hidden="true" />
              Público
            </Badge>
          ) : (
            <Badge variant="secondary">
              <Lock className="w-3 h-3" aria-hidden="true" />
              Privado
            </Badge>
          )}
        </div>

        <h3 className="mb-2 font-semibold text-foreground">{pool.name}</h3>

        {pool.description && (
          <p className="mb-4 text-sm text-muted-foreground line-clamp-2">
            {pool.description}
          </p>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" aria-hidden="true" />
          <span>
            {pool.memberCount || 0}{' '}
            {pool.memberCount === 1 ? 'participante' : 'participantes'}
          </span>
        </div>
      </Card>
    </Link>
  );
}
